import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Rocket, Music, Users, DollarSign, Clock, Loader2, Check, Play, Plus, Target, Zap, Star } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../App";
import { toast } from "sonner";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_ICONS = { "Supporter": Star, "Backer": Zap, "VIP": Target, "Executive Producer": Rocket };

export default function CrowdfundPage() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPledge, setShowPledge] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [pledging, setPledging] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "", description: "", preview_url: "", genre: "", goal_amount: 500
  });

  const headers = (() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  })();

  const fetchCampaigns = () => {
    axios.get(`${API}/api/crowdfund/`)
      .then(res => setCampaigns(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const loadCampaign = async (c) => {
    try {
      const res = await axios.get(`${API}/api/crowdfund/${c.campaign_id}`);
      setSelected(res.data);
    } catch { setSelected(c); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to create"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/crowdfund/`, createForm, { headers });
      toast.success("Campaign created!");
      setShowCreate(false);
      setCreateForm({ title: "", description: "", preview_url: "", genre: "", goal_amount: 500 });
      fetchCampaigns();
      loadCampaign(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setCreating(false); }
  };

  const handlePledge = async () => {
    if (!user) { toast.error("Sign in to pledge"); return; }
    if (!selectedTier) { toast.error("Select a tier"); return; }
    setPledging(true);
    try {
      const res = await axios.post(`${API}/api/crowdfund/${selected.campaign_id}/pledge`, {
        amount: selectedTier.amount, tier: selectedTier.name
      }, { headers });
      toast.success(`Pledged $${selectedTier.amount}! ${res.data.funded ? "Campaign funded!" : ""}`);
      setShowPledge(false);
      setSelectedTier(null);
      loadCampaign(selected);
      fetchCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to pledge"); }
    finally { setPledging(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="crowdfund-page">
      <SEO title="Fan-Backed Beats" description="Back your favorite producers. Get exclusive rewards when beats drop." />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-neon-magenta/20 text-neon-magenta border-0 mb-3">
            <Rocket className="w-3 h-3 mr-1" /> Crowdfund
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-3">
            Fan-Backed <span className="text-neon-magenta">Beats</span>
          </h1>
          <p className="text-zinc-500 max-w-lg mx-auto text-sm">
            Back producers you believe in. Get exclusive access when the beat drops.
          </p>
          {user && (
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="mt-4 bg-neon-magenta hover:bg-neon-magenta/80 text-black font-bold" data-testid="create-campaign-btn">
                  <Plus className="w-4 h-4 mr-2" /> Launch Campaign
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">Launch Your Campaign</DialogTitle></DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <Label>Beat Title</Label>
                    <Input value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                      className="bg-black/50 border-white/10" required data-testid="campaign-title-input" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                      className="bg-black/50 border-white/10" rows={3} data-testid="campaign-desc-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Preview Audio URL</Label>
                      <Input value={createForm.preview_url} onChange={e => setCreateForm({ ...createForm, preview_url: e.target.value })}
                        className="bg-black/50 border-white/10" placeholder="https://..." data-testid="campaign-preview-input" />
                    </div>
                    <div>
                      <Label>Genre</Label>
                      <Input value={createForm.genre} onChange={e => setCreateForm({ ...createForm, genre: e.target.value })}
                        className="bg-black/50 border-white/10" placeholder="Trap, R&B..." data-testid="campaign-genre-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Funding Goal ($)</Label>
                    <Input type="number" value={createForm.goal_amount}
                      onChange={e => setCreateForm({ ...createForm, goal_amount: parseInt(e.target.value) || 500 })}
                      className="bg-black/50 border-white/10" min={50} max={50000} data-testid="campaign-goal-input" />
                  </div>
                  <Button type="submit" disabled={creating} className="w-full bg-neon-magenta text-black font-bold" data-testid="launch-campaign-btn">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
                    Launch Campaign
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Campaign Grid */}
        {campaigns.length === 0 ? (
          <div className="glass border border-white/10 rounded-xl p-16 text-center max-w-md mx-auto">
            <Rocket className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 mb-2">No campaigns yet</p>
            <p className="text-zinc-600 text-xs">Be the first producer to launch one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {campaigns.map(c => (
              <CampaignCard key={c.campaign_id} campaign={c} onClick={() => loadCampaign(c)} isSelected={selected?.campaign_id === c.campaign_id} />
            ))}
          </div>
        )}

        {/* Campaign Detail Modal */}
        {selected && (
          <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
            <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="campaign-detail-modal">
              <div className="space-y-5">
                {/* Header */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`text-[10px] ${selected.status === "active" ? "bg-neon-green/20 text-neon-green" : selected.status === "funded" ? "bg-neon-magenta/20 text-neon-magenta" : "bg-zinc-700 text-zinc-400"}`}>
                      {selected.status === "funded" ? "FUNDED" : selected.status}
                    </Badge>
                    {selected.genre && <Badge className="bg-white/10 text-zinc-300 text-[10px]">{selected.genre}</Badge>}
                  </div>
                  <h2 className="font-heading text-2xl uppercase" data-testid="campaign-detail-title">{selected.title}</h2>
                  <p className="text-xs text-zinc-500 mt-1">by {selected.producer_name}</p>
                </div>

                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-neon-magenta font-bold">${selected.current_amount}</span>
                    <span className="text-zinc-500">of ${selected.goal_amount} goal</span>
                  </div>
                  <Progress value={selected.progress_pct || 0} className="h-3 bg-zinc-800" data-testid="campaign-progress" />
                  <div className="flex justify-between text-xs text-zinc-500">
                    <span>{selected.backer_count || 0} backers</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(selected.deadline).toLocaleDateString()}</span>
                  </div>
                </div>

                <p className="text-sm text-zinc-300">{selected.description}</p>

                {/* Preview */}
                {selected.preview_url && (
                  <a href={selected.preview_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg bg-neon-cyan/5 border border-neon-cyan/20 hover:bg-neon-cyan/10 transition"
                  >
                    <Play className="w-5 h-5 text-neon-cyan" />
                    <span className="text-sm text-neon-cyan font-medium">Listen to Preview</span>
                  </a>
                )}

                {/* Tiers */}
                <div>
                  <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Backing Tiers</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(selected.tiers || []).map((tier, i) => {
                      const TierIcon = TIER_ICONS[tier.name] || Star;
                      return (
                        <button key={i}
                          onClick={() => { setSelectedTier(tier); setShowPledge(true); }}
                          className={`text-left p-4 rounded-xl border transition hover:bg-white/5 ${
                            selectedTier?.name === tier.name ? "border-neon-magenta bg-neon-magenta/5" : "border-white/10"
                          }`}
                          data-testid={`tier-${tier.name.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <TierIcon className="w-4 h-4 text-neon-magenta" />
                            <span className="text-sm font-medium text-white">{tier.name}</span>
                          </div>
                          <p className="text-xl font-bold text-neon-magenta mb-1">${tier.amount}</p>
                          <p className="text-[11px] text-zinc-400">{tier.reward}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Pledge Button */}
                {selected.status === "active" && user && (
                  <Dialog open={showPledge} onOpenChange={setShowPledge}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-neon-magenta hover:bg-neon-magenta/80 text-black font-bold py-6" data-testid="back-this-beat-btn">
                        <DollarSign className="w-4 h-4 mr-2" /> Back This Beat
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-white/10 max-w-sm">
                      <DialogHeader><DialogTitle className="font-heading">Confirm Your Pledge</DialogTitle></DialogHeader>
                      <div className="space-y-4 py-2">
                        {selectedTier ? (
                          <div className="p-4 rounded-lg bg-neon-magenta/5 border border-neon-magenta/20 text-center">
                            <p className="text-xs text-zinc-500 uppercase mb-1">{selectedTier.name}</p>
                            <p className="text-3xl font-bold text-neon-magenta">${selectedTier.amount}</p>
                            <p className="text-xs text-zinc-400 mt-1">{selectedTier.reward}</p>
                          </div>
                        ) : (
                          <p className="text-zinc-500 text-sm text-center">Select a tier first</p>
                        )}
                        <Button onClick={handlePledge} disabled={pledging || !selectedTier}
                          className="w-full bg-neon-magenta text-black font-bold" data-testid="confirm-pledge-btn"
                        >
                          {pledging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                          Confirm Pledge
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Backers */}
                {selected.backers && selected.backers.length > 0 && (
                  <div>
                    <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Recent Backers</h3>
                    <div className="space-y-2">
                      {selected.backers.slice(0, 10).map(b => (
                        <div key={b.pledge_id} className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/40">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-neon-magenta/20 flex items-center justify-center">
                              <Users className="w-3.5 h-3.5 text-neon-magenta" />
                            </div>
                            <div>
                              <p className="text-xs text-white">{b.user_name}</p>
                              <p className="text-[10px] text-zinc-500">{b.tier}</p>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-neon-magenta">${b.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function CampaignCard({ campaign: c, onClick, isSelected }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`text-left w-full glass border rounded-xl overflow-hidden transition hover:bg-white/5 ${
        isSelected ? "border-neon-magenta/50" : "border-white/10"
      }`}
      data-testid={`campaign-card-${c.campaign_id}`}
    >
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Badge className={`text-[10px] ${c.status === "active" ? "bg-neon-green/20 text-neon-green" : c.status === "funded" ? "bg-neon-magenta/20 text-neon-magenta" : "bg-zinc-700 text-zinc-400"}`}>
            {c.status === "funded" ? "FUNDED" : c.status}
          </Badge>
          {c.genre && <span className="text-[10px] text-zinc-500">{c.genre}</span>}
        </div>
        <div>
          <h3 className="font-medium text-white text-sm">{c.title}</h3>
          <p className="text-[11px] text-zinc-500">by {c.producer_name}</p>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-neon-magenta font-bold">${c.current_amount}</span>
            <span className="text-zinc-500">${c.goal_amount} goal</span>
          </div>
          <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full bg-neon-magenta rounded-full transition-all" style={{ width: `${c.progress_pct || 0}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.backer_count} backers</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(c.deadline).toLocaleDateString()}</span>
        </div>
      </div>
    </motion.button>
  );
}
